/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useFormikContext } from 'formik'
import { S3_URL } from '@configs/config'
import { useAlert } from '@hooks/useAlert'
import { WriterFormType } from '@models/writer/WriterFormType'
import { getErrorMessage } from '@lib/utils'
import { BankAcccountInformationProps, BankType } from './interface'

export function withBankAccountInformation(
  Component: React.FC<BankAcccountInformationProps>
) {
  function WithBankAccountInformation() {
    const [banks, setBanks] = useState<BankType[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const alert = useAlert()
    const { values, setFieldValue } = useFormikContext<WriterFormType>()

    useEffect(() => {
      function fetchBanks() {
        setIsLoading(true)
        fetch(`${S3_URL}/bank-information.json`)
          .then(res => res.json())
          .then(res => setBanks(res))
          .catch(error => alert.error(getErrorMessage(error)))
          .finally(() => setIsLoading(false))
      }

      fetchBanks()
    }, [])

    const handleBankChange = useCallback(
      (value: string) => {
        const data = banks.find(item => item.bankCode === value)
        setFieldValue('bankAccount.bankName', data?.bankName)
        setFieldValue('bankAccount.bankBranch', '')
        setFieldValue('bankAccount.bankBranchCode', '')
        setFieldValue('bankAccount.bankProvince', '')
      },
      [banks]
    )

    const handleBankBranchChange = useCallback(
      (value: string) => {
        const data = banks.find(
          item => item.bankCode === values.bankAccount.bankCode
        )
        const branch = data?.branch?.find(b => b.bankBranchCode === value)
        if (branch) {
          setFieldValue('bankAccount.bankBranch', branch.bankBranchName)
          setFieldValue('bankAccount.bankProvince', branch.branchProvince)
        }
      },
      [banks, values.bankAccount.bankCode]
    )

    const bankOptions = useMemo(() => {
      return banks.map(item => ({
        text: item.bankName,
        value: item.bankCode,
      }))
    }, [banks])

    const branchOptions = useMemo(() => {
      const data = banks.find(
        item => item.bankCode === values.bankAccount.bankCode
      )
      if (data) {
        return data.branch.map(item => ({
          text: `${item.bankBranchCode} (${item.bankBranchName})`,
          value: item.bankBranchCode,
        }))
      }

      return []
    }, [banks, values.bankAccount.bankCode])

    if (isLoading) {
      return null
    }

    const componentProps = {
      bankOptions,
      branchOptions,
      bankCode: values.bankAccount.bankCode,
      handleBankChange,
      handleBankBranchChange,
    }
    return <Component {...componentProps} />
  }

  return WithBankAccountInformation
}
